import {defineStore} from 'pinia'
import type {OutputUavMission, OutputUavMissionNote, OutputUavMissionShootdown, GetUavMissionsModeEnum} from '~/models'

interface UavMissionNotification {
  type: string
  action: 'created' | 'updated' | 'deleted'
  missionId: number
  payload?: OutputUavMission
}

interface UavMissionNoteNotification {
  type: string
  action: 'created'
  missionId: number
  payload?: OutputUavMissionNote
}

// Єдиний порядок для HTTP і realtime: новіші заявки завжди зверху.
// Не покладаємось лише на ORDER BY бекенда, бо Centrifugo патчить масив без нового GET.
function newestMissionsFirst(items: OutputUavMission[]): OutputUavMission[] {
  return [...items].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
}

export const useUavMissionsStore = defineStore('uavMissions', {
  state: () => ({
    missions: [] as OutputUavMission[],
    notesByMission: {} as Record<number, OutputUavMissionNote[]>,
    notesLoaded: {} as Record<number, boolean>,
    // Кількість нових нотаток (з елемента/треду), що прилетіли по Centrifugo, поки тред
    // цієї заявки був згорнутий. Живе лише в пам'яті вкладки — це індикатор "подивись сюди",
    // а не персистентний unread-стан як у чаті.
    unreadNotes: {} as Record<number, number>,
    // Які треди зараз розгорнуті — нотатки, що приходять у відкритий тред, юзер бачить одразу,
    // тож індикатор для них не піднімаємо.
    openThreads: {} as Record<number, boolean>,
    // id нотаток, які вже приходили по Centrifugo в цій вкладці. Потрібні, бо ту саму нотатку
    // ми отримуємо більш ніж один раз: спільна dev-БД шле pg_notify ВСІМ підписаним бекендам
    // (локальний + задеплоєний), і кожен публікує в Centrifugo свою копію. Для 'uav_mission' це
    // непомітно (заміна об'єкта ідемпотентна), а інкрементний лічильник давав би +2 за одне
    // повідомлення. Дедуп по id тримаємо незалежно від того, чи завантажений тред.
    seenNoteIds: [] as number[],
    // Остання нотатка, що прилетіла по Centrifugo, по кожній заявці — джерело для
    // короткочасної хмаринки біля літачка на мапі (DispatchMap). at (мітка часу приходу)
    // потрібна саме як тригер: мапа показує лише записи, новіші за вже показані.
    noteFlashes: {} as Record<number, {id?: number, body: string, authorName: string, at: number}>,
  }),
  actions: {
    // mode/search/silent — ті самі параметри, що приймає GET /api/uav-missions.
    async load(params: {mode?: GetUavMissionsModeEnum, search?: string, silent?: boolean} = {}) {
      const response = await useApi({silent: params.silent}).getUavMissionsRaw({
        mode: params.mode,
        search: params.search,
        limit: 1000,
        offset: 0,
        sortBy: 'id',
        orderBy: 'desc',
      })
      this.missions = newestMissionsFirst(await response.value())

      // Лічильники непрочитаних беремо з бекенду (uav_mission_note_reads), інакше після F5
      // маркер гаснув би на нотатках, які користувач так і не відкривав. Відкриті зараз треди
      // не чіпаємо — там усе вже прочитане, і серверне число прийшло б із запізненням.
      for (const mission of this.missions) {
        if (!mission.id || this.openThreads[mission.id]) continue
        this.unreadNotes[mission.id] = mission.unreadNotesCount ?? 0
      }
    },

    // Оптимістичний локальний патч статусу (без запиту) — для миттєвої реакції UI
    // (підсвітка кнопки, переміщення літака на мапі) до відповіді сервера.
    // statusChangedAt виставляємо тут же (той самий "зараз", що напише сервер) — це прибирає
    // потребу в подальшому silent reloadList, який раніше підміняв об'єкт місії ще раз і викликав
    // видимий "перемальовується" ефект на картці (StatusButtons більше не смикає reloadList).
    // Повертає попередній статус+час — використовується для відкату при помилці запиту.
    patchMissionStatus(missionId: number, status: string): {status?: string, statusChangedAt?: string} | undefined {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return undefined
      const previous = {status: this.missions[idx].status, statusChangedAt: this.missions[idx].statusChangedAt}
      this.missions[idx] = {...this.missions[idx], status, statusChangedAt: new Date().toISOString()}
      return previous
    },

    // Відкат після помилки запиту зміни статусу — повертає status+statusChangedAt
    // до знімку, знятого перед оптимістичним патчем.
    restoreMissionStatus(missionId: number, previous: {status?: string, statusChangedAt?: string}) {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return
      this.missions[idx] = {...this.missions[idx], ...previous}
    },

    // Оптимістичне зняття тривоги РЕБ (кнопка на картці — екіпаж доповів, що подавлення
    // більше немає). Пульсація борта на мапі гасне одразу, запит іде у фоні.
    // Повертає попередній момент доповіді — для відкату при помилці запиту.
    clearMissionEwReport(missionId: number): {ewReportedAt?: string} | undefined {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return undefined
      const previous = {ewReportedAt: this.missions[idx].ewReportedAt}
      this.missions[idx] = {...this.missions[idx], ewReportedAt: undefined}
      return previous
    },

    // Відкат після помилки запиту зняття тривоги РЕБ.
    restoreMissionEwReport(missionId: number, previous: {ewReportedAt?: string}) {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return
      this.missions[idx] = {...this.missions[idx], ...previous}
    },

    // Оптимістичний локальний append нового запису в журнал збитих бортів (+ статус
    // awaiting_launch, як в AddUavMissionShootdownHandler: екіпаж піднімає резервний борт і
    // продовжує ту саму заявку) — миттєва реакція UI до відповіді сервера.
    // Повертає попередній список+статус — використовується для відкату при помилці запиту.
    addMissionShootdown(missionId: number, coords: string): {shootdowns: OutputUavMissionShootdown[], status?: string} | undefined {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return undefined
      const previous = {shootdowns: this.missions[idx].shootdowns ?? [], status: this.missions[idx].status}
      const entry: OutputUavMissionShootdown = {
        seq: previous.shootdowns.length + 1,
        coords,
        occurredAt: new Date().toISOString(),
        reportedBy: 0,
        reportedByName: '',
      }
      this.missions[idx] = {...this.missions[idx], shootdowns: [...previous.shootdowns, entry], status: 'awaiting_launch'}
      return previous
    },

    // Оптимістичне видалення останнього запису журналу (undo помилкового внесення).
    // Статус НЕ відкочується (як в DeleteLastUavMissionShootdownHandler).
    removeLastMissionShootdown(missionId: number): OutputUavMissionShootdown[] | undefined {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return undefined
      const previous = this.missions[idx].shootdowns ?? []
      this.missions[idx] = {...this.missions[idx], shootdowns: previous.slice(0, -1)}
      return previous
    },

    // Відкат після помилки запиту addMissionShootdown/removeLastMissionShootdown —
    // повертає журнал (і за потреби статус) до знятого раніше знімку.
    restoreMissionShootdowns(missionId: number, shootdowns: OutputUavMissionShootdown[], status?: string) {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return
      this.missions[idx] = {...this.missions[idx], shootdowns, ...(status ? {status} : {})}
    },

    // Тред розгорнули/згорнули. Розгортання гасить індикатор одразу (локально) і фіксує
    // позначку "прочитано" на сервері — щоб маркер не повернувся після перезавантаження.
    setThreadOpen(missionId: number, open: boolean) {
      this.openThreads[missionId] = open
      if (!open) return

      this.unreadNotes[missionId] = 0
      this.markNotesRead(missionId)
    },

    // Позначка "прочитано" — fire-and-forget: маркер уже згас локально, і збій запиту не має
    // ні ламати розгортання треду, ні кидати помилку в UI (silent). Найгірший наслідок —
    // маркер повернеться після F5.
    async markNotesRead(missionId: number) {
      try {
        await useApi({silent: true}).markUavMissionNotesRead({id: missionId})
      } catch {
        // навмисно проковтуємо
      }
    },

    async loadNotes(missionId: number) {
      const notes = await useApi().getUavMissionNotes({id: missionId})
      this.notesByMission[missionId] = notes
      this.notesLoaded[missionId] = true
    },

    handleCentrifugeNotification(message: UavMissionNotification | UavMissionNoteNotification) {
      if (!message) return

      if (message.type === 'uav_mission') {
        const {action, missionId, payload} = message as UavMissionNotification
        switch (action) {
          case 'created':
          case 'updated':
            if (payload) {
              const idx = this.missions.findIndex(m => m.id === missionId)
              if (idx >= 0) {
                const updated = [...this.missions]
                // Толерантність до мішаних версій бекенду під час rollout: спільна dev-БД
                // (render.com) шле pg_notify ВСІМ підписаним бекендам, і старий задеплоєний
                // інстанс без фічі shootdowns транслює payload, де поля просто немає (undefined,
                // не []). Новий бекенд завжди шле масив (хай і порожній). Тож undefined трактуємо
                // як "відправник не знає про фічу" і зберігаємо локальний журнал — інакше старі
                // broadcast'и затирали б shootdowns у нуль, і chip блимав би on/off (toggle).
                updated[idx] = payload.shootdowns === undefined
                    ? {...payload, shootdowns: this.missions[idx].shootdowns}
                    : payload
                this.missions = newestMissionsFirst(updated)
              } else {
                this.missions = newestMissionsFirst([...this.missions, payload])
              }
            }
            break
          case 'deleted':
            this.missions = this.missions.filter(m => m.id !== missionId)
            break
        }
        return
      }

      if (message.type === 'uav_mission_note') {
        const {missionId, payload} = message as UavMissionNoteNotification
        if (!payload) return

        const notes = this.notesByMission[missionId] || []
        // Дедуплікація: та сама нотатка приходить кілька разів (кілька бекендів на спільній БД),
        // плюс власна нотатка (щойно створена через addNote) прилітає і сюди. Ні в список,
        // ні в лічильник її вдруге не пускаємо.
        if (payload.id !== undefined) {
          if (this.seenNoteIds.includes(payload.id)) return
          // Вікно пам'яті обмежене — вкладка може жити добами, список не має рости без меж.
          this.seenNoteIds = [...this.seenNoteIds.slice(-499), payload.id]
        }
        if (this.notesLoaded[missionId] && notes.some(n => n.id === payload.id)) return

        // Хмаринка біля літачка — після дедупу, щоб одна й та сама нотатка не блимала двічі.
        // Ставимо незалежно від стану треду: на мапі чергового відкритої лівої панелі може
        // й не бути в полі зору.
        this.noteFlashes[missionId] = {
          id: payload.id,
          body: payload.body ?? '',
          authorName: payload.authorName ?? '',
          at: Date.now(),
        }

        // Індикатор піднімаємо навіть якщо тред ще жодного разу не відкривали (нотаток у
        // пам'яті немає) — саме це і є "нове повідомлення з елемента" на згорнутій картці.
        if (this.openThreads[missionId]) {
          // Тред відкритий — нотатку видно одразу, тож зсуваємо серверну позначку вперед.
          // Інакше після F5 вона повернулась би як непрочитана.
          this.markNotesRead(missionId)
        } else {
          this.unreadNotes[missionId] = (this.unreadNotes[missionId] ?? 0) + 1
        }

        // Тред не розкривали — не тримаємо нотатки в пам'яті, вони підвантажаться при toggle().
        if (!this.notesLoaded[missionId]) return
        this.notesByMission[missionId] = [...notes, payload]
      }
    },
  },
})
